import { useCallback, useContext, useEffect } from 'react';

import { push } from 'connected-react-router';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import Button from 'src/app/components/Elements/Button';
import LinkButton from 'src/app/components/Elements/Button/LinkButton';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import Spinner from 'src/app/components/Elements/Spinner';
import { useLazySegmentTrack } from 'src/app/hooks/useLazySegmentTrack';
import type { MedicationCardContextType } from 'src/app/providers/MedicationCardStateProvider';
import { MedicationCardStateContext } from 'src/app/providers/MedicationCardStateProvider';
import { medicationDetailsForTransfer, medicationGuides, medications } from 'src/app/router/paths';
import { segmentTrack, setShowNewArchivedPageModal } from 'src/app/state/actions';
import { archiveItem } from 'src/app/state/actions/homescreen';
import {
  clearMedicationDetailsError,
  fetchMedicationDetails,
  setCurrentPrescriptionId,
} from 'src/app/state/actions/medicationDetails';
import { fetchMedicationGuideForNDC } from 'src/app/state/actions/medicationGuides';
import {
  getCurrentAccountId,
  getCurrentMedicationDetailErrorResponse,
  getCurrentMedicationDetailPatientEducation,
  getCurrentMedicationDetailPrescription,
  getCurrentMedicationDetailPrescriptionLifecycle,
  getCurrentMedicationDetailProvider,
  getCurrentMedicationDetailWarningLabels,
  getCurrentMedicationGuideHtml,
  getCurrentPathname,
  getHasMedicationDetailsLoaded,
  getPrescriptionIdInPathParam,
} from 'src/app/state/selectors';
import { getIsMedArchived, loadArchivedMedications } from 'src/app/state/slices/archivedMedications';
import { useDispatch, useSelector } from 'src/app/state/store';
import { arePathnamesEqual, formatPhoneNumber } from 'src/app/utils';
import { REFILL_STATUSES } from 'src/consts';

import { getUITextForDeliveryAddress } from '../Checkout/utils';

import { CurrentPrescriptionSection } from './Sections/CurrentPrescriptionSection';
import { ImportantInformationSection } from './Sections/ImportantInformationSection';
import { OverviewSection } from './Sections/OverviewSection';
import { PrescriberSection } from './Sections/PrescriberSection';
import { SectionsContainer } from './styles';

export const MedicationDetails = (props: OwnPropsType) => {
  const dispatch = useDispatch();
  const dispatchFetchArchivedPrescriptions = useCallback(() => dispatch(loadArchivedMedications()), [dispatch]);
  const dispatchSetShowNewArchivedPageModal = useCallback(
    (showModal: boolean) => dispatch(setShowNewArchivedPageModal(showModal)),
    [dispatch]
  );

  const { medicationCardState } = useContext(MedicationCardStateContext) as MedicationCardContextType;

  const accountId = useSelector(getCurrentAccountId);
  const currentMedicationDetailErrorResponse = useSelector(getCurrentMedicationDetailErrorResponse);
  const medicationDetailsPrescription = useSelector(getCurrentMedicationDetailPrescription);
  const hasMedicationDetailsLoaded = useSelector(getHasMedicationDetailsLoaded);
  const providerInfo = useSelector(getCurrentMedicationDetailProvider);
  const warningLabels = useSelector(getCurrentMedicationDetailWarningLabels);
  const patientEducation = useSelector(getCurrentMedicationDetailPatientEducation);
  const archivedMed = useSelector(state => getIsMedArchived(state, props));
  const prescriptionId = useSelector(state => getPrescriptionIdInPathParam(state, props));
  const ndcMedGuideHtml = useSelector(state => getCurrentMedicationGuideHtml(state, props));
  const prescriptionLifecycle = useSelector(getCurrentMedicationDetailPrescriptionLifecycle);

  const pathname = useSelector(getCurrentPathname);
  const isInTransferDetails = arePathnamesEqual(pathname, medicationDetailsForTransfer.toPath({ prescriptionId }));

  useEffect(() => {
    if (prescriptionId) {
      if (!medicationDetailsPrescription) {
        dispatch(
          fetchMedicationDetails({
            prescriptionId,
            accountId,
            isInTransferDetails,
            useLocalRequestRefillReceivedState:
              medicationCardState?.prescriptionIdsInRequestRefillState?.includes(prescriptionId),
            useLocalProcessRefillReceivedState:
              medicationCardState?.prescriptionIdsInProcessRefillState?.includes(prescriptionId),
          })
        );
        dispatchFetchArchivedPrescriptions();
      }

      dispatch(setCurrentPrescriptionId(prescriptionId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, dispatch, medicationDetailsPrescription, prescriptionId, dispatchFetchArchivedPrescriptions]);

  useEffect(() => {
    dispatch(loadArchivedMedications());
  }, [dispatch]);

  useEffect(() => {
    if (hasMedicationDetailsLoaded && currentMedicationDetailErrorResponse) {
      dispatch(
        segmentTrack(UNIFIED.medicationDetailFailed, {
          prescriptionId,
          error_code: currentMedicationDetailErrorResponse?.errorCode,
          error_summary: currentMedicationDetailErrorResponse?.flavorText,
        })
      );
      dispatch(clearMedicationDetailsError());
      dispatch(push(medications.toPath()));
    }
  }, [
    dispatch,
    currentMedicationDetailErrorResponse,
    hasMedicationDetailsLoaded,
    medicationDetailsPrescription,
    patientEducation,
    prescriptionId,
    providerInfo,
    warningLabels,
  ]);

  useEffect(() => {
    dispatch(
      segmentTrack(UNIFIED.viewMedicationDetails, {
        is_archived: !!archivedMed,
        patient_medications_id: medicationDetailsPrescription?.patientMedicationId,
      })
    );
  }, [dispatch, archivedMed, medicationDetailsPrescription]);

  const [trackArchiveMedication] = useLazySegmentTrack(UNIFIED.archiveMedication);
  const [trackUnarchiveMedication] = useLazySegmentTrack(UNIFIED.unarchiveMedication);

  const { firstName, lastName, phone, mailingAddress } = providerInfo || {};
  const { firstRowText: firstAddressRowText, secondRowText: secondAddressRowText } = getUITextForDeliveryAddress(
    mailingAddress,
    null
  );

  const { ndc } = medicationDetailsPrescription || {};

  useEffect(() => {
    if (ndc) dispatch(fetchMedicationGuideForNDC(ndc));
  }, [dispatch, ndc]);

  const toggleArchiveButtonClicked = useCallback(async () => {
    const patientMedicationId = archivedMed?.patientMedicationId || medicationDetailsPrescription?.patientMedicationId;
    const reloadDrugDetails = archivedMed;

    const response = await dispatch(archiveItem(patientMedicationId, archivedMed));

    if (response) {
      await dispatch(loadArchivedMedications());
      dispatchSetShowNewArchivedPageModal(true);

      if (archivedMed) {
        trackUnarchiveMedication({
          source: 'Medication Details',
          is_expired: archivedMed.refillStatusText === REFILL_STATUSES.EXPIRED,
          patientmedication_id: patientMedicationId,
          medication_id: archivedMed.medicationId,
        });
      } else {
        trackArchiveMedication({
          source: 'Medication Details',
          is_expired: medicationDetailsPrescription.refillStatusText === REFILL_STATUSES.EXPIRED,
          patientmedication_id: patientMedicationId,
          medication_id: medicationDetailsPrescription.medicationId,
        });
      }
    }
    if (reloadDrugDetails) {
      dispatch(
        fetchMedicationDetails({
          prescriptionId,
          accountId,
          isInTransferDetails,
          useLocalRequestRefillReceivedState:
            medicationCardState?.prescriptionIdsInRequestRefillState?.includes(prescriptionId),
          useLocalProcessRefillReceivedState:
            medicationCardState?.prescriptionIdsInProcessRefillState?.includes(prescriptionId),
        })
      );
    }
  }, [
    accountId,
    dispatch,
    dispatchSetShowNewArchivedPageModal,
    archivedMed,
    medicationDetailsPrescription,
    prescriptionId,
    trackArchiveMedication,
    trackUnarchiveMedication,
  ]);

  const prescriptionDetails = archivedMed ? archivedMed : medicationDetailsPrescription;

  return (
    <StandardPage title={'Details'}>
      {!hasMedicationDetailsLoaded ? (
        <Spinner centered />
      ) : (
        <SectionsContainer>
          <OverviewSection
            medicationDetailsPrescription={prescriptionDetails}
            prescriptionLifecycle={prescriptionLifecycle}
          />
          {prescriptionLifecycle && prescriptionDetails?.prescribedDate && prescriptionDetails?.expirationDate && (
            <CurrentPrescriptionSection prescriptionDetails={prescriptionDetails} />
          )}

          {providerInfo && (
            <PrescriberSection
              name={`${firstName} ${lastName}`}
              phoneNumber={formatPhoneNumber(phone)}
              firstAddressRowText={firstAddressRowText}
              secondAddressRowText={secondAddressRowText}
            />
          )}
          {(warningLabels?.length > 0 || patientEducation?.length > 0) && (
            <ImportantInformationSection warningLabels={warningLabels} patientEducation={patientEducation} />
          )}

          {ndcMedGuideHtml && (
            <LinkButton
              size={'mediumSmallMargin'}
              style="tertiary"
              to={medicationGuides.toPath({ ndc })}
              onClick={null}
            >
              Full Medication Details
            </LinkButton>
          )}
          {!isInTransferDetails && (
            <>
              <br />
              <Button size={'medium'} style="secondary" block onClick={toggleArchiveButtonClicked}>
                {archivedMed ? 'Unarchive Medication' : 'Archive Medication'}
              </Button>
            </>
          )}
        </SectionsContainer>
      )}
    </StandardPage>
  );
};
